import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { auth, db, storage } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, addDoc, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { canPost } from '../config/allowlist';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_IMAGES = 6;

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#F7F5F0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
  },
  card: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E0DDD6',
    borderRadius: '8px',
    padding: '2.5rem',
    width: '100%',
    maxWidth: '720px',
  },
  header: {
    marginBottom: '1.5rem',
  },
  title: {
    margin: 0,
    fontFamily: 'Playfair Display, Georgia, serif',
    fontSize: '32px',
    color: '#111110',
  },
  subtitle: {
    margin: '0.5rem 0 0',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '14px',
    color: '#888880',
  },
  label: {
    display: 'block',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '13px',
    color: '#444441',
    marginBottom: '0.4rem',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #E0DDD6',
    borderRadius: '4px',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '14px',
    color: '#111110',
    backgroundColor: '#FFFFFF',
    marginBottom: '1rem',
    outline: 'none',
  },
  textarea: {
    width: '100%',
    minHeight: '120px',
    padding: '10px 12px',
    border: '1px solid #E0DDD6',
    borderRadius: '4px',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '14px',
    color: '#111110',
    backgroundColor: '#FFFFFF',
    marginBottom: '1rem',
    outline: 'none',
    resize: 'vertical',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #E0DDD6',
    borderRadius: '4px',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '14px',
    color: '#111110',
    backgroundColor: '#FFFFFF',
    marginBottom: '1rem',
  },
  submitBtn: {
    width: '100%',
    padding: '12px',
    border: 'none',
    borderRadius: '4px',
    backgroundColor: '#4A9E8A',
    color: '#F7F5F0',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '14px',
    cursor: 'pointer',
    marginBottom: '1rem',
  },
  secondaryBtn: {
    width: '100%',
    padding: '12px',
    border: '1px solid #E0DDD6',
    borderRadius: '4px',
    backgroundColor: '#FFFFFF',
    color: '#111110',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '14px',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  note: {
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '13px',
    color: '#888880',
    marginBottom: '1rem',
  },
  error: {
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '13px',
    color: '#E24B4A',
    marginBottom: '1rem',
    textAlign: 'center',
  },
  success: {
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '13px',
    color: '#336633',
    marginBottom: '1rem',
    textAlign: 'center',
  },
  uploadArea: {
    border: '2px dashed #E0DDD6',
    borderRadius: '8px',
    padding: '2rem',
    textAlign: 'center',
    marginBottom: '1rem',
    cursor: 'pointer',
    transition: 'border-color 0.2s',
  },
  uploadAreaHover: {
    borderColor: '#4A9E8A',
  },
  uploadIcon: {
    fontSize: '32px',
    marginBottom: '0.5rem',
    color: '#888880',
  },
  imageGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '0.75rem',
    marginBottom: '1rem',
  },
  imagePreview: {
    position: 'relative',
    aspectRatio: '1',
    borderRadius: '8px',
    overflow: 'hidden',
    border: '2px solid transparent',
    cursor: 'pointer',
  },
  imagePreviewSelected: {
    borderColor: '#4A9E8A',
  },
  imagePreviewImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  thumbnailBadge: {
    position: 'absolute',
    bottom: '4px',
    left: '4px',
    backgroundColor: '#4A9E8A',
    color: '#FFFFFF',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '10px',
    padding: '2px 6px',
    borderRadius: '4px',
  },
  removeBtn: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: 'rgba(0,0,0,0.6)',
    color: '#FFFFFF',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
  },
  progressBar: {
    width: '100%',
    height: '8px',
    backgroundColor: '#E0DDD6',
    borderRadius: '4px',
    overflow: 'hidden',
    marginBottom: '1rem',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#4A9E8A',
    transition: 'width 0.3s',
  },
};

// Image processing functions
async function loadImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

function centerCrop(img, targetWidth, targetHeight) {
  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');

  const imgAspect = img.width / img.height;
  const targetAspect = targetWidth / targetHeight;

  let sx, sy, sw, sh;

  if (imgAspect > targetAspect) {
    sh = img.height;
    sw = sh * targetAspect;
    sx = (img.width - sw) / 2;
    sy = 0;
  } else {
    sw = img.width;
    sh = sw / targetAspect;
    sx = 0;
    sy = (img.height - sh) / 2;
  }

  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, targetWidth, targetHeight);
  return canvas;
}

function containResize(img, maxWidth) {
  const canvas = document.createElement('canvas');
  const scale = maxWidth / img.width;
  canvas.width = maxWidth;
  canvas.height = img.height * scale;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas;
}

async function processImage(file, variant) {
  const img = await loadImage(file);
  let canvas;

  switch (variant) {
    case 'thumbnail':
      canvas = centerCrop(img, 400, 400);
      break;
    case 'gallery':
      canvas = centerCrop(img, 1200, 800);
      break;
    case 'full':
      canvas = containResize(img, 2000);
      break;
    default:
      canvas = containResize(img, 1200);
  }

  // Convert to WebP with compression
  const quality = variant === 'thumbnail' ? 0.75 : variant === 'gallery' ? 0.8 : 0.85;
  const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/webp', quality));
  return blob;
}

function PostListing() {
  const { id } = useParams();
  const [category, setCategory] = useState('stays');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [price, setPrice] = useState('');
  const [priceType, setPriceType] = useState('night');
  const [images, setImages] = useState([]);
  const [thumbnailIndex, setThumbnailIndex] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [existingListing, setExistingListing] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthChecked(true);
    });
    return () => unsubscribe();
  }, []);

  // Load existing listing if editing
  useEffect(() => {
    const loadListing = async () => {
      if (!id || !user) return;
      try {
        const docRef = doc(db, 'listings', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          // Only allow editing own listings
          if (data.userId !== user.uid) {
            setError('You can only edit your own listings.');
            return;
          }
          setExistingListing(data);
          setIsEditing(true);
          setCategory(data.category);
          setTitle(data.title);
          setDescription(data.description);
          setLocation(data.location);
          setPrice(data.price.toString());
          setPriceType(data.priceType || 'night');
          setThumbnailIndex(data.thumbnailIndex || 0);
          // Set images array with existing images marked
          if (data.images && data.images.length > 0) {
            const existingImages = data.images.map((img) => ({
              file: null,
              preview: img.thumbnail,
              existing: true,
              full: img.full,
              gallery: img.gallery,
              thumbnail: img.thumbnail
            }));
            setImages(existingImages);
          }
        } else {
          setError('Listing not found.');
        }
      } catch (err) {
        console.error('Error loading listing:', err);
        setError('Unable to load listing. Please try again.');
      }
    };
    loadListing();
  }, [id, user]);

  const resetForm = () => {
    setCategory('stays');
    setTitle('');
    setDescription('');
    setLocation('');
    setPrice('');
    setPriceType('night');
    setImages([]);
    setThumbnailIndex(0);
    setUploadProgress(0);
  };

  const validateFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Only JPEG, PNG, and WebP images are allowed.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'Image must be less than 10 MB.';
    }
    return null;
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const currentCount = images.filter(img => !img.existing).length;
    if (currentCount + files.length > MAX_IMAGES) {
      setError(`You can only upload up to ${MAX_IMAGES} images.`);
      return;
    }

    const newImages = [];
    for (const file of files) {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }
      newImages.push({
        file,
        preview: URL.createObjectURL(file),
        processing: false,
        existing: false
      });
    }

    setImages(prev => [...prev, ...newImages]);
    setError('');
  };

  const removeImage = (index) => {
    URL.revokeObjectURL(images[index].preview);
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
    if (thumbnailIndex >= newImages.length) {
      setThumbnailIndex(Math.max(0, newImages.length - 1));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!user) {
      setError('You need to be logged in to post a listing.');
      return;
    }
    if (!title.trim() || !description.trim() || !location.trim() || !price.trim()) {
      setError('Please complete all text fields.');
      return;
    }
    if (images.length === 0) {
      setError('Please upload at least one image.');
      return;
    }
    if (isNaN(price) || Number(price) <= 0) {
      setError('Please enter a valid price.');
      return;
    }

    setLoading(true);
    setUploadProgress(0);

    try {
      const listingId = isEditing ? id : `${user.uid}_${Date.now()}`;
      const uploadedImages = [];

      // Process only new images, keep existing ones
      const imagesToProcess = images.filter(img => !img.existing);
      const existingImages = images.filter(img => img.existing);

      for (let i = 0; i < imagesToProcess.length; i++) {
        const imgData = imagesToProcess[i];

        // Process variants
        const thumbnailBlob = await processImage(imgData.file, 'thumbnail');
        const galleryBlob = await processImage(imgData.file, 'gallery');
        const fullBlob = await processImage(imgData.file, 'full');

        // Upload to Firebase Storage
        const basePath = `listings/${listingId}/${existingImages.length + i}`;

        const [thumbRef, galleryRef, fullRef] = [
          ref(storage, `${basePath}_thumbnail.webp`),
          ref(storage, `${basePath}_gallery.webp`),
          ref(storage, `${basePath}_full.webp`)
        ];

        await Promise.all([
          uploadBytes(thumbRef, thumbnailBlob),
          uploadBytes(galleryRef, galleryBlob),
          uploadBytes(fullRef, fullBlob)
        ]);

        const [thumbUrl, galleryUrl, fullUrl] = await Promise.all([
          getDownloadURL(thumbRef),
          getDownloadURL(galleryRef),
          getDownloadURL(fullRef)
        ]);

        uploadedImages.push({
          thumbnail: thumbUrl,
          gallery: galleryUrl,
          full: fullUrl,
          isThumbnail: existingImages.length + i === thumbnailIndex
        });

        setUploadProgress(Math.round(((i + 1) / imagesToProcess.length) * 100));
      }

      // Combine existing images with new ones
      const allImages = [
        ...existingImages.map((img, idx) => ({
          thumbnail: img.thumbnail,
          gallery: img.gallery,
          full: img.full,
          isThumbnail: idx === thumbnailIndex
        })),
        ...uploadedImages
      ];

      const listingData = {
        category,
        title: title.trim(),
        description: description.trim(),
        location: location.trim(),
        price: Number(price),
        priceType,
        images: allImages,
        thumbnailIndex,
        status: existingListing?.status || 'Active',
        viewCount: existingListing?.viewCount || 0,
      };

      if (isEditing) {
        // Update existing listing
        await updateDoc(doc(db, 'listings', id), {
          ...listingData,
          updatedAt: serverTimestamp(),
        });
        resetForm();
        setSuccess('Your listing has been updated successfully.');
        navigate('/account');
      } else {
        // Create new listing
        await addDoc(collection(db, 'listings'), {
          ...listingData,
          userId: user.uid,
          userEmail: user.email || '',
          userName: user.displayName || '',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        resetForm();
        setSuccess('Your listing has been posted successfully.');
        navigate('/');
      }
    } catch (err) {
      console.error('PostListing error', err);
      setError('Unable to save listing. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (authChecked && !user) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.header}>
            <h1 style={styles.title}>{isEditing ? 'Edit listing' : 'Post a listing'}</h1>
            <p style={styles.subtitle}>Please log in to {isEditing ? 'edit' : 'create'} a listing.</p>
          </div>
          <p style={styles.error}>You need an account to {isEditing ? 'edit' : 'post on'} HostelChic.</p>
          <Link to="/login" style={styles.secondaryBtn}>Log in</Link>
        </div>
      </div>
    );
  }

  if (authChecked && user && !canPost(user)) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.header}>
            <h1 style={styles.title}>Access restricted</h1>
            <p style={styles.subtitle}>Posting is currently limited to approved accounts.</p>
          </div>
          <p style={styles.error}>Your account ({user.email}) is not approved to post listings at this time.</p>
          <Link to="/" style={styles.secondaryBtn}>Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.title}>{isEditing ? 'Edit listing' : 'Post a listing'}</h1>
          <p style={styles.subtitle}>
            {isEditing
              ? 'Update your listing details. Select one image as the thumbnail.'
              : 'Upload up to 6 photos. Select one as the thumbnail.'}
          </p>
        </div>

        {error && <p style={styles.error}>{error}</p>}
        {success && <p style={styles.success}>{success}</p>}

        {loading && (
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: `${uploadProgress}%` }} />
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <label style={styles.label}>Category</label>
          <select
            style={styles.select}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="stays">Stays</option>
            <option value="marketplace">Marketplace</option>
          </select>

          <label style={styles.label}>Title</label>
          <input
            style={styles.input}
            type="text"
            placeholder="Simple, clear headline"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />

          <label style={styles.label}>Description</label>
          <textarea
            style={styles.textarea}
            placeholder="Describe the space or item, condition, location, and any details buyers should know"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
          />

          <label style={styles.label}>Location</label>
          <input
            style={styles.input}
            type="text"
            placeholder="City, neighborhood, or pickup area"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            required
          />

          <label style={styles.label}>Price</label>
          <div style={{ display: 'grid', gridTemplateColumns: category === 'stays' ? '2fr 1fr' : '1fr', gap: '0.75rem' }}>
            <input
              style={styles.input}
              type="number"
              placeholder="Price in USD"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              required
              min="0"
              step="0.01"
            />
            {category === 'stays' && (
              <select
                style={styles.select}
                value={priceType}
                onChange={(e) => setPriceType(e.target.value)}
              >
                <option value="night">per night</option>
                <option value="week">per week</option>
                <option value="biweek">per 2 weeks</option>
                <option value="month">per month</option>
              </select>
            )}
          </div>

          <label style={styles.label}>Photos (up to 6)</label>
          <p style={styles.note}>JPEG, PNG, WebP. Max 10 MB each. Click an image to set as thumbnail.</p>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={handleImageChange}
            style={{ display: 'none' }}
          />

          {images.length < MAX_IMAGES && (
            <div
              style={styles.uploadArea}
              onClick={() => fileInputRef.current?.click()}
            >
              <div style={styles.uploadIcon}>📷</div>
              <p style={{ margin: 0, fontFamily: 'DM Sans, system-ui, sans-serif', fontSize: '14px', color: '#555553' }}>
                Click to upload photos
              </p>
              <p style={{ margin: '0.25rem 0 0', fontFamily: 'DM Sans, system-ui, sans-serif', fontSize: '12px', color: '#888880' }}>
                {images.length}/{MAX_IMAGES} photos {isEditing ? 'selected' : 'uploaded'}
              </p>
            </div>
          )}

          {images.length > 0 && (
            <div style={styles.imageGrid}>
              {images.map((img, index) => (
                <div
                  key={index}
                  style={{
                    ...styles.imagePreview,
                    ...(index === thumbnailIndex ? styles.imagePreviewSelected : {})
                  }}
                  onClick={() => setThumbnailIndex(index)}
                >
                  <img
                    src={img.preview}
                    alt={`Preview ${index + 1}`}
                    style={styles.imagePreviewImg}
                  />
                  {index === thumbnailIndex && (
                    <span style={styles.thumbnailBadge}>Thumbnail</span>
                  )}
                  {img.existing && (
                    <span style={{ ...styles.thumbnailBadge, bottom: '4px', left: 'auto', right: '4px', backgroundColor: '#888880' }}>
                      Existing
                    </span>
                  )}
                  <button
                    style={styles.removeBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeImage(index);
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          <button style={styles.submitBtn} type="submit" disabled={loading || images.length === 0}>
            {loading ? (isEditing ? 'Updating listing...' : 'Posting listing...') : (isEditing ? 'Update listing' : 'Post listing')}
          </button>
        </form>
      </div>
    </div>
  );
}

export default PostListing;